import { ElementType, Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import PromoterGuard from 'src/guards/PromoterGuard';
import StoreGuard from 'src/guards/StoreGuard';
import StoreLayout from 'src/layouts/StoreLayout';
import LoadingScreen from '../components/LoadingScreen';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: <Navigate to="/promoter/login" replace />,
    },
    {
      path: '/promoter',
      children: [
        { element: <Navigate to="/promoter/login" replace />, index: true },
        {
          path: 'area',
          element: (
            <PromoterGuard>
              <PromoterArea />
            </PromoterGuard>
          ),
        },
        { path: 'login', element: <PromoterLogin /> },
        // { path: 'bonus/:membercard', element: <PromoterGuard><CreateBonus /></PromoterGuard> }, // escondendo funcionalidade conforme pedido do cliente
        {
          path: 'benefits-list',
          element: (
            <PromoterGuard>
              <BenefitsList />
            </PromoterGuard>
          ),
        },
        {
          path: 'user-benefits/:membercard',
          element: (
            <PromoterGuard>
              <BenefitsList />
            </PromoterGuard>
          ),
        },
        {
          path: 'membercard/:page/:benefitId/:benefitType',
          element: (
            <PromoterGuard>
              <Membercard />
            </PromoterGuard>
          ),
        },
        {
          path: 'reservation-confirm/:benefitId/:membercard/:benefitType',
          element: (
            <PromoterGuard>
              <ReservationConfirm />
            </PromoterGuard>
          ),
        },
        {
          path: 'benefit-details/:benefitId/:benefitType',
          element: (
            <PromoterGuard>
              <BenefitDetails />
            </PromoterGuard>
          ),
        },
        {
          path: 'without-reservation/:benefitId/:membercard/:benefitType',
          element: (
            <PromoterGuard>
              <WithoutReservation />
            </PromoterGuard>
          ),
        },
        {
          path: 'lar-center/:membercard',
          element: (
            <PromoterGuard>
              <LarCenter />
            </PromoterGuard>
          ),
        },
        {
          path: 'satisfaction-survey/:benefitId/:benefitType',
          element: (
            <PromoterGuard>
              <SatisfactionSurvey />
            </PromoterGuard>
          ),
        },
      ],
    },
    {
      path: '/store',
      children: [
        { element: <Navigate to="/store/login" replace />, index: true },
        { path: 'login', element: <StoreLogin /> },
      ],
    },
    {
      path: '/store',
      element: (
        <StoreGuard>
          <StoreLayout />
        </StoreGuard>
      ),
      children: [
        { element: <Navigate to="/store/area" replace />, index: true },
        { path: 'area', element: <StoreArea /> },
        { path: 'benefits-list', element: <StoreBenefitsList /> },
        { path: 'membercard/:page/:benefitId/:benefitType', element: <StoreMembercard /> },
        {
          path: 'reservation-confirm/:benefitId/:membercard/:benefitType',
          element: <StoreReservationConfirm />,
        },
        { path: 'benefit-details/:benefitId/:benefitType', element: <StoreBenefitDetails /> },
        {
          path: 'without-reservation/:benefitId/:membercard/:benefitType',
          element: <StoreWithoutReservation />,
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Dashboard
const PromoterLogin = Loadable(lazy(() => import('../pages/promoter/pages/PromoterLogin')));
const PromoterArea = Loadable(lazy(() => import('../pages/promoter/pages/PromoterArea')));
// const CreateBonus = Loadable(lazy(() => import('../pages/promoter/pages/CreateBonus')));
const BenefitsList = Loadable(lazy(() => import('../pages/promoter/pages/BenefitsList')));
const Membercard = Loadable(lazy(() => import('../pages/promoter/pages/Membercard')));
const ReservationConfirm = Loadable(
  lazy(() => import('../pages/promoter/pages/ReservationConfirm'))
);
const BenefitDetails = Loadable(lazy(() => import('../pages/promoter/pages/BenefitDetails')));
const WithoutReservation = Loadable(
  lazy(() => import('../pages/promoter/pages/WithoutReservation'))
);
const LarCenter = Loadable(lazy(() => import('../pages/promoter/pages/LarCenter')));
const SatisfactionSurvey = Loadable(
  lazy(() => import('../pages/promoter/pages/SatisfactionSurvey'))
);
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// Store
const StoreLogin = Loadable(lazy(() => import('../pages/store/pages/StoreLogin')));
const StoreArea = Loadable(lazy(() => import('../pages/store/pages/StoreArea')));
const StoreBenefitsList = Loadable(lazy(() => import('../pages/store/pages/BenefitsList')));
const StoreMembercard = Loadable(lazy(() => import('../pages/store/pages/Membercard')));
const StoreReservationConfirm = Loadable(
  lazy(() => import('../pages/store/pages/ReservationConfirm'))
);
const StoreBenefitDetails = Loadable(lazy(() => import('../pages/store/pages/BenefitDetails')));
const StoreWithoutReservation = Loadable(
  lazy(() => import('../pages/store/pages/WithoutReservation'))
);
